import { motion } from "framer-motion";

import Marquee from "../components/Marquee";
import { AnimatedTestimonialsDemo } from "../components/Testimonial";
import React, { useState, useEffect, useRef } from "react";
import PickupAvailableImg from "../assets/pickup_img.png";
import { brands, homeServices, homeStats } from "./constants";
import About from "./About";

const CountUp = ({ value }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;
          let currentCount = 0;
          const increment = value / 50;
          const timer = setInterval(() => {
            currentCount += increment;
            if (currentCount >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(Math.floor(currentCount));
            }
          }, 20);
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.1 }
    );
    if (countRef.current) {
      observer.observe(countRef.current);
    }
    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [value]);
  return <span ref={countRef}>{count}</span>;
};

const Home = () => {
  const [showIntro, setShowIntro] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const hasSeenIntro = sessionStorage.getItem("hasSeenIntro");
    if (!hasSeenIntro) {
      setShowIntro(true);
      if (videoRef.current) {
        videoRef.current.play().catch((error) => {
          console.log("Autoplay failed:", error);
        });
      }
    }
  }, []);

  const handleVideoEnd = () => {
    setShowIntro(false);
    sessionStorage.setItem("hasSeenIntro", "true");
  };

  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowIntro(false);
    sessionStorage.setItem("hasSeenIntro", "true");
  };

  return (
    <div className="flex flex-col min-h-screen">
      {showIntro && (
        <div
          style={{ zIndex: 1000 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-95 transition-all duration-500"
        >
          <video
            ref={videoRef}
            src={require("../assets/cameraIntro.mp4")}
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnd}
            className="w-full h-full object-cover"
            style={{ maxHeight: "100vh", maxWidth: "100vw" }}
          />
          <button
            onClick={handleSkip}
            className="absolute top-8 right-8 z-50 px-6 py-2 rounded bg-white/80 text-black font-semibold text-lg hover:bg-white/90 shadow-lg"
          >
            Skip
          </button>
        </div>
      )}
      {!showIntro && (
        <>
          <Marquee />

          {/* Services Section */}
          <section className="py-20 bg-muted/30">
            <div className="container px-4 mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold tracking-tight mb-4">
                  Our Services
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Camera services to keep your equipment in perfect working
                  condition. Our expert technicians provide comprehensive care
                  for all your photography needs.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {homeServices.map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-background p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="p-3 rounded-full bg-primary/10 text-primary w-fit mb-4">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
              <div className="flex flex-col md:flex-row gap-8 items-stretch justify-center my-12">
                {/* Pickup & Delivery Service (Left) */}
                <div className="flex flex-col items-center md:items-start bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors shadow-md  p-6 w-full md:w-1/2">
                  <img
                    src={PickupAvailableImg}
                    alt="Camera Service Pickup Available"
                    className="w-40 h-[fit-content]"
                  />
                  <div className="font-semibold text-lg">
                    <span className="font-bold">Pickup & Delivery Service</span>
                    <br />
                    <p
                      className="text-muted-foreground mt-2"
                      style={{ fontSize: "15px" }}
                    >
                      We offer convenient{" "}
                      <span className="font-bold text-lg">
                        Pick up and Delivery
                      </span>{" "}
                      for all camera services. Whether your camera needs repair,
                      cleaning, or calibration, our team will collect your
                      equipment from your doorstep and deliver it back to you
                      once the service is complete.
                      <br />
                      {/* <span className="font-bold text-lg">
                        Pre-owned cameras
                      </span>{" "}
                      purchased from us will also be delivered safely to your
                      address, ensuring a hassle-free experience from start to
                      finish. */}
                    </p>
                  </div>
                </div>
                {/* Supported Brands (Right) */}
                <div className="flex flex-col items-center justify-center bg-muted/50 rounded-lg hover:bg-muted/70 rounded-lg shadow-md p-6 w-full md:w-1/2">
                  <h2 className="text-2xl font-bold mb-4 text-primary">
                    Supported Brands
                  </h2>
                  <div className="flex flex-col md:flex-row flex-wrap justify-center gap-5 w-full items-center">
                    {brands.map((brand, index) => (
                      <motion.div
                        key={brand.name}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: index * 0.08 }}
                        className="px-5 py-2 bg-background rounded-full shadow border border-primary/20 text-primary font-semibold text-base tracking-wide hover:bg-muted/70 transition-colors duration-200 flex items-center gap-2"
                        style={{ textAlign: "center" }}
                      >
                        <img
                          src={brand.img}
                          alt={brand.name + " logo"}
                          style={{
                            width: 28,
                            height: 28,
                            objectFit: "contain",
                            marginRight: 8,
                            background: "white",
                            borderRadius: "50%",
                          }}
                        />
                        {brand.name}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <About />

          <AnimatedTestimonialsDemo />

          {/* Stats Section */}
          <section className="py-20 bg-muted/50">
            <div className="container px-4 mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {homeStats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col items-center text-center p-6 bg-background rounded-lg shadow-sm"
                  >
                    <div className="p-3 rounded-full bg-primary/10 text-primary mb-4">
                      {stat.icon}
                    </div>
                    <h3 className="text-3xl font-bold">
                      {stat.value !== 1 ? (
                        <>
                          {" "}
                          <CountUp value={stat.value} />+
                        </>
                      ) : (
                        "1M+"
                      )}
                    </h3>
                    <p className="mt-2 text-muted-foreground">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Home;
